import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import {
    completeEmailCampaignThunk,
    fetchEmailCampaignQuoteThunk,
    sendEmailCampaignNowThunk
} from '@spike/email-marketing-action';
import AdyenPayment, { CompletedInfo } from 'components/Payments/Adyen/AdyenPayment';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import Button from 'components/UI/V3/Button/Button';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import { useApiClientWrapper, useMasterData } from 'hooks';
import moment from 'moment';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

import { isSenderReadyForSend } from '../isSenderReadyForSend';
import { EmailCampaignFormValues } from '../types';

import styles from './SendConfirmationModal.module.scss';

interface SendConfirmationModalProps {
    values: EmailCampaignFormValues;
    /**
     * Resolved recipient count post-exclusions — same number passed to
     * `TotalClientsBar` in Step 1. Used to refresh the quote on open.
     */
    recipientTotal: number;
    onCancel: () => void;
    onSubmitted: (mode: 'sent' | 'scheduled') => void;
    /**
     * Opens the wizard-root EmailSender drawer. Wired from the "Manage sender"
     * link inside the unverified-sender alert below; the click cancels this
     * modal first so the user lands directly in the sender configuration UI
     * instead of seeing a modal stacked behind a drawer.
     */
    onOpenSenderDrawer: () => void;
}

/**
 * Final commit point of the campaign wizard. The UI structure depends on three
 * orthogonal axes:
 *
 *   - `master_data.emailCampaignsBillingEnabled` — global gate, set by BE via
 *     the `EMAIL_CAMPAIGNS_BILLING_ENABLED` env flag (CUD-4799). When `false`,
 *     campaigns are free at MVP launch: no Price section, no Adyen drop-in,
 *     the "Send" button hits `POST /email_campaign/send_now/:id` directly.
 *     When `true`, the original Adyen-paid path renders unchanged.
 *
 *   - `values.sendTime` — wizard-local choice between immediate and scheduled
 *     sending. The "Schedule" branch is still a mock (Ticket F pending) — it
 *     does not call BE in either billing mode.
 *
 *   - `marketplaceEmailSender` — when the marketplace has an enabled-but-not-
 *     verified custom sender, the BE blocks every send with a 422. The modal
 *     pre-empts that path: it shows an alert with a link to manage the sender
 *     and hides every send affordance (Adyen drop-in + primary buttons) so the
 *     user never reaches a 422 from this surface.
 *
 * Quote is refetched on open with `recipientTotal` only in paid mode; in free
 * mode the quote is irrelevant and we skip the network call entirely.
 */
const SendConfirmationModal: React.FC<SendConfirmationModalProps> = ({
    values,
    recipientTotal,
    onCancel,
    onSubmitted,
    onOpenSenderDrawer
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { emailCampaignsBillingEnabled } = useMasterData();

    const quote = useSelector((state: RootState) => state.emailCampaigns.quote.data);
    const quoteLoading = useSelector((state: RootState) => state.emailCampaigns.quote.loading);
    const sender = useSelector((state: RootState) => state.emailMarketingSender.data);

    const [adyenError, setAdyenError] = React.useState<string | null>(null);
    const [sendNowError, setSendNowError] = React.useState<string | null>(null);
    const [sending, setSending] = React.useState<boolean>(false);

    // Send requires a verified custom sender per the mandatory-sender rule.
    // Any state other than `enabled && verified && senderEmail` triggers the
    // block: unconfigured (never set up), pending (verification in flight),
    // or failed. The alert body copy diverges between those cases below —
    // see `renderSenderAlertBody`.
    const senderBlocking = !isSenderReadyForSend(sender);

    React.useEffect(() => {
        // Quote is only meaningful when billing is on. In free mode the user
        // never sees a price, so don't waste a round-trip on a quote we won't
        // render.
        if (!emailCampaignsBillingEnabled) return;
        if (recipientTotal > 0) {
            dispatch(fetchEmailCampaignQuoteThunk(apiClientWrapper, recipientTotal));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAdyenComplete = async (info: CompletedInfo) => {
        if (!info.success) {
            setAdyenError(info.message || 'Payment was not completed.');
            return;
        }
        if (values.campaignId == null) {
            setAdyenError('Campaign id missing — please reload and try again.');
            return;
        }

        const result = await (dispatch(
            completeEmailCampaignThunk(apiClientWrapper, values.campaignId)
        ) as unknown as Promise<unknown>);

        if (result) {
            onSubmitted('sent');
        }
    };

    const handleAdyenError = (message: string) => {
        setAdyenError(message);
    };

    const handleScheduleConfirm = () => {
        onSubmitted('scheduled');
    };

    /**
     * Commit point for the free-send path (billing disabled). Calls the BE
     * `send_now` endpoint and, on success, transitions to the submitted
     * screen. On failure the thunk already toasts via `alertErrorHandler`;
     * we surface a tighter inline message in case the user missed the toast.
     *
     * Defensive: if BE responds with 422 because billing has been re-enabled
     * server-side but the marketplace's `masterData` is stale, the inline
     * error tells the user to reload — we do NOT auto-fall-back to Adyen
     * because the wizard's footer + payment sections aren't mounted.
     */
    const handleFreeSend = async () => {
        if (values.campaignId == null) {
            setSendNowError('Campaign id missing — please reload and try again.');
            return;
        }
        setSendNowError(null);
        setSending(true);
        const result = await (dispatch(
            sendEmailCampaignNowThunk(apiClientWrapper, values.campaignId)
        ) as unknown as Promise<unknown>);
        setSending(false);
        if (result) {
            onSubmitted('sent');
        } else {
            setSendNowError('Unable to send the campaign. Please reload and try again.');
        }
    };

    // Bridge from the "Manage sender" link to the wizard root. Cancelling
    // first dismisses this modal so the user only ever sees one full-screen
    // takeover at a time (cleaner than stacking modals).
    const handleManageSender = () => {
        onCancel();
        onOpenSenderDrawer();
    };

    // Sender-blocking alert body — copy diverges by whether the marketplace
    // has opted in at all (`enabled`) vs opted in but not yet verified
    // (`pending` / `failed`). Rendered inside the same `<AlertMessage>` shell
    // in both compact and wide layouts, so the divergent JSX lives here.
    const renderSenderAlertBody = () => {
        if (!sender?.enabled) {
            return (
                <span>
                    You need to configure a verified sender before you can send this campaign.{' '}
                    <button
                        type="button"
                        className={styles.senderLink}
                        onClick={handleManageSender}
                    >
                        Set up sender
                    </button>
                </span>
            );
        }
        return (
            <span>
                Your custom sender (<strong>{sender.senderEmail}</strong>) isn&apos;t verified yet. Verify it before you
                can send this campaign.{' '}
                <button
                    type="button"
                    className={styles.senderLink}
                    onClick={handleManageSender}
                >
                    Manage sender
                </button>
            </span>
        );
    };

    // Three commit affordances based on billing mode + sendTime:
    //   - billing OFF + 'now'       → "Send" (calls send_now directly)
    //   - billing ANY + 'scheduled' → "Schedule" (mock, Ticket F pending)
    //   - billing ON  + 'now'       → no primary button; AdyenPayment renders
    //                                 the pay flow which fires onComplete →
    //                                 dispatches `complete` automatically.
    // When `senderBlocking` is true every primary affordance disappears — only
    // Cancel remains, since the user must first resolve the sender state.
    //
    // `compact` flips footer styling between the wide billing-enabled layout
    // (right-aligned, fixed min-width buttons) and the V3 confirm-family layout
    // (full-width balanced buttons + size="large"), mirroring the swap on the
    // modal body above.
    const renderFooter = (compact: boolean) => {
        const footerClass = compact ? styles.compactFooter : styles.footer;
        const buttonClass = compact ? styles.compactFooterButton : styles.footerButton;
        const buttonSize = compact ? 'large' : undefined;

        if (senderBlocking) {
            return (
                <div className={footerClass}>
                    <Button
                        size={buttonSize}
                        label="Cancel"
                        variant={compact ? 'black' : 'outlined'}
                        onClick={onCancel}
                        className={buttonClass}
                    />
                </div>
            );
        }

        if (values.sendTime === 'scheduled') {
            return (
                <div className={footerClass}>
                    <Button
                        size={buttonSize}
                        label="Cancel"
                        variant={compact ? 'black' : 'outlined'}
                        onClick={onCancel}
                        className={buttonClass}
                    />
                    <Button
                        size={buttonSize}
                        label="Schedule"
                        variant="green"
                        onClick={handleScheduleConfirm}
                        className={buttonClass}
                    />
                </div>
            );
        }

        if (!emailCampaignsBillingEnabled) {
            return (
                <div className={footerClass}>
                    <Button
                        size={buttonSize}
                        label="Cancel"
                        variant={compact ? 'black' : 'outlined'}
                        onClick={onCancel}
                        className={buttonClass}
                    />
                    <Button
                        size={buttonSize}
                        label="Send"
                        variant="green"
                        onClick={handleFreeSend}
                        disabled={sending}
                        className={buttonClass}
                    />
                </div>
            );
        }

        return (
            <div className={footerClass}>
                <Button
                    size={buttonSize}
                    label="Cancel"
                    variant={compact ? 'black' : 'outlined'}
                    onClick={onCancel}
                    className={buttonClass}
                />
            </div>
        );
    };

    // Compact layout = V3 confirm-family (UseCuddlesDefault / ResetCommission /
    // ExitWithoutSaving): centred prose body, 506px rounded modal, balanced
    // full-width buttons. We use it when the modal has nothing to host beyond
    // the confirmation itself — i.e. when billing is OFF (no quote/Adyen).
    const useCompactLayout = !emailCampaignsBillingEnabled;

    if (useCompactLayout) {
        return (
            <GenericModal
                className={styles.modalCompact}
                onClose={onCancel}
                footer={renderFooter(true)}
            >
                <div className={styles.compactBody}>
                    <h2 className={styles.compactTitle}>{buildConfirmationPrompt(values)}</h2>

                    {senderBlocking ? (
                        <AlertMessage
                            icon={faInfoCircle}
                            iconColor="#BAA997"
                            bdColor="#BCB8AE"
                            bgColor="#F8F5F1"
                        >
                            {renderSenderAlertBody()}
                        </AlertMessage>
                    ) : (
                        <p className={styles.compactDescription}>
                            {buildConfirmationDescription(values, recipientTotal)}
                        </p>
                    )}

                    {sendNowError && <p className={styles.error}>{sendNowError}</p>}
                </div>
            </GenericModal>
        );
    }

    // Wide layout (billing-enabled) — keeps the existing structure with the
    // title + Price + Payment sections. We don't repeat the Step 3 review
    // card here either; the audience+timing summary is folded into the
    // prose description shown next to the title so the wide modal stays
    // operationally clear without flooding the user with redundant rows.
    return (
        <GenericModal
            className={styles.modal}
            onClose={onCancel}
            footer={renderFooter(false)}
        >
            <div className={styles.body}>
                <h2 className={styles.title}>{buildConfirmationPrompt(values)}</h2>
                <p className={styles.wideDescription}>{buildConfirmationDescription(values, recipientTotal)}</p>

                {senderBlocking && (
                    <AlertMessage
                        icon={faInfoCircle}
                        iconColor="#BAA997"
                        bdColor="#BCB8AE"
                        bgColor="#F8F5F1"
                    >
                        {renderSenderAlertBody()}
                    </AlertMessage>
                )}

                {emailCampaignsBillingEnabled && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionHeading}>Price</h3>
                        {quote ? (
                            <table className={styles.priceTable}>
                                <tbody>
                                    <tr>
                                        <td>
                                            {quote.recipients.toLocaleString()} emails × $
                                            {(quote.subtotal / Math.max(1, quote.recipients)).toFixed(2)}
                                        </td>
                                        <td className={styles.priceCell}>${quote.subtotal.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td>Tax</td>
                                        <td className={styles.priceCell}>${quote.tax.toFixed(2)}</td>
                                    </tr>
                                    <tr className={styles.totalRow}>
                                        <td>Total</td>
                                        <td className={styles.priceCell}>${quote.total.toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.priceLoading}>
                                {quoteLoading ? 'Loading quote…' : 'Quote not available.'}
                            </div>
                        )}
                    </div>
                )}

                {emailCampaignsBillingEnabled && values.sendTime === 'now' && !senderBlocking && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionHeading}>Payment</h3>
                        {values.campaignId != null && (
                            <AdyenPayment
                                campaignId={values.campaignId}
                                onComplete={handleAdyenComplete}
                                onError={handleAdyenError}
                            />
                        )}
                        {adyenError && <p className={styles.error}>{adyenError}</p>}
                    </div>
                )}

                {emailCampaignsBillingEnabled && values.sendTime === 'scheduled' && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionHeading}>Payment</h3>
                        <p className={styles.paymentHint}>Charged to your saved payment method on file.</p>
                    </div>
                )}

                {!emailCampaignsBillingEnabled && sendNowError && <p className={styles.error}>{sendNowError}</p>}
            </div>
        </GenericModal>
    );
};

/**
 * Short, action-focused title. The operational details (audience size + when)
 * render as two compact label/value rows under the title — see the `.summary`
 * block in the modal body — so the question itself stays scannable.
 */
const buildConfirmationPrompt = (values: EmailCampaignFormValues): string => {
    const name = values.campaignName?.trim() || 'this campaign';
    const verb = values.sendTime === 'scheduled' ? 'Schedule' : 'Send';
    return `${verb} "${name}"?`;
};

/**
 * Single-sentence prose summary of WHO will receive the campaign and WHEN —
 * matches the V3 confirm-family description pattern (UseCuddlesDefault /
 * ResetCommission) instead of a label/value row block. Singular/plural is
 * handled per branch so "1 client" / "20 clients" / "1 specific client" /
 * "20 specific clients" all read naturally.
 *
 * Scheduled branch breaks the "on <date> at <time>." fragment onto its own
 * line so the timestamp is visually anchored under the audience clause
 * instead of wrapping mid-sentence at modal widths.
 */
const buildConfirmationDescription = (values: EmailCampaignFormValues, recipientTotal: number): React.ReactNode => {
    const isInclude = Array.isArray(values.selectedClientIds);
    const noun = recipientTotal === 1 ? 'client' : 'clients';
    const recipientLabel = `${recipientTotal.toLocaleString()} ${isInclude ? 'specific ' : ''}${noun}`;

    if (values.sendTime === 'scheduled') {
        const combined =
            values.scheduledDate && values.scheduledTime
                ? moment(`${values.scheduledDate} ${values.scheduledTime}`, 'YYYY-MM-DD HH:mm', true)
                : null;
        const when = combined && combined.isValid() ? combined.format('MMM D, YYYY [at] h:mm A') : 'the scheduled time';
        return (
            <>
                <span className={styles.descriptionEmphasis}>{recipientLabel}</span> will receive this email
                <br />
                <span className={styles.descriptionEmphasis}>on {when}.</span>
            </>
        );
    }

    return (
        <>
            <span className={styles.descriptionEmphasis}>{recipientLabel}</span> will receive this email immediately.
        </>
    );
};

export default SendConfirmationModal;
